import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Button, 
  Avatar, 
  Typography,
  Drawer,
  Tabs
} from 'antd';
import { MenuOutlined, HomeOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import Header from '../../../components/Common/Layout/Header';
import PanelLegalLinks from '../../../components/Common/PanelLegalLinks';
import { apiService } from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import { MenteeProfileResponse } from '../../../types';
import styles from './MenteePanel.module.css';
import { 
  UserOutlined, 
  CrownOutlined,
  BookOutlined,
  ProjectOutlined,
  SettingOutlined,
  StarOutlined,
  LaptopOutlined,
  ClockCircleOutlined,
  UsergroupAddOutlined,
  FileTextOutlined,
  CalendarOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import ProfileTab from './ProfileTab';
import EducationTab from './EducationTab';
import ProjectTab from './ProjectTab';
import SkillTab from './SkillTab';
import WorkExperienceTab from './WorkExperienceTab';
import CertificateTab from './CertificateTab';
import OneOnOneRequestsTab from './OneOnOneRequestsTab';
import BootcampRequestsTab from './BootcampRequestsTab';
import JobApplicationsTab from './JobApplicationsTab';
import InterviewsTab from './InterviewsTab';
import ReferenceRequestsTab from './ReferenceRequestsTab';
import IncomingReferenceRequestsTab from './IncomingReferenceRequestsTab';
import MentorRatingTab from './MentorRatingTab';
import ReceivedReferencesTab from './ReceivedReferencesTab';
import ReferanslarTab from './ReferanslarTab';
import SubscriptionTab from './SubscriptionTab';
import SettingsTab from './SettingsTab';
import SubscriptionProtectedRoute from '../../../components/Common/SubscriptionProtectedRoute';
import PanelTabCard from '../../../components/Common/PanelTabCard';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const MenteePanel: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMenu, setSelectedMenu] = useState('profile');
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuVisible(false);
  };

  const { data: profileData } = useQuery({
    queryKey: menteeProfileQueryKeys.profile,
    queryFn: async () => {
      const response = await apiService.getMenteeProfile();
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['subscription', 'job-applications', 'interviews', 'appointments', 'profile', 'referanslar', 'education', 'projects', 'skills', 'work', 'certificates', 'settings'].includes(tab)) {
      setSelectedMenu(tab);
      if (tab !== 'settings' && tab !== 'job-applications' && tab !== 'interviews') {
        const next = new URLSearchParams(searchParams);
        next.delete('tab');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  const menuItems = [
    { key: 'profile', label: 'Profil Bilgileri', icon: <UserOutlined /> },
    { key: 'subscription', label: 'Abonelik Bilgileri', icon: <CrownOutlined /> },
    { key: 'settings', label: 'Güvenlik Ayarları', icon: <SettingOutlined /> },
    { key: 'education', label: 'Eğitim Bilgileri', icon: <BookOutlined /> },
    { key: 'projects', label: 'Projeler', icon: <ProjectOutlined /> },
    { key: 'skills', label: 'Yetkinlikler', icon: <SettingOutlined /> },
    { key: 'work', label: 'İş Hayatı', icon: <LaptopOutlined /> },
    { key: 'certificates', label: 'Sertifikalar', icon: <StarOutlined /> },
    { key: 'job-applications', label: 'İş Başvurularım', icon: <FileTextOutlined /> },
    { key: 'interviews', label: 'Mülakatlarım', icon: <CalendarOutlined /> },
    { key: 'referanslar', label: 'Referanslar', icon: <StarOutlined /> },
    { key: 'appointments', label: 'Randevular', icon: <ClockCircleOutlined /> }
  ];

  const handleMenuClick = (key: string) => {
    setSelectedMenu(key);
    setMobileMenuVisible(false);
  };

  const renderSidebar = () => (
    <>
      <div className="panel-sidebar-brand">
        <Avatar
          size={64}
          style={{ backgroundColor: 'var(--primary-600)', color: '#fff', fontSize: '20px', fontWeight: 'bold' }}
          src={profileData?.profileImage ? `data:image/jpeg;base64,${profileData.profileImage}` : undefined}
        >
          {profileData ? `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(0)}` : 'AY'}
        </Avatar>
        <h2 className="panel-user-name">{profileData ? `${profileData.firstName} ${profileData.lastName}` : 'Yükleniyor...'}</h2>
        <span className="panel-user-role"><UserOutlined style={{ marginRight: 4 }} /> Mentee</span>
        {(profileData?.email || user?.email) && (
          <span className="panel-user-email">{profileData?.email || user?.email}</span>
        )}
      </div>
      <nav className="panel-nav">
        {menuItems.map((item) => (
          <button
            type="button"
            key={item.key}
            className={`panel-nav-item ${selectedMenu === item.key ? 'panel-nav-item--active' : ''}`}
            onClick={() => handleMenuClick(item.key)}
          >
            <span className="panel-nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
      <div className="panel-sidebar-footer">
        <PanelLegalLinks />
        <button type="button" className="panel-logout" onClick={handleLogout}>
          <LogoutOutlined /> Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <Layout className={styles.profileContainer}>
      <Header />

      {/* Mobil: sticky panel header – Admin/Company ile aynı */}
      <header className={styles.panelHeaderMobile}>
        <div className="panel-header-left">
          <Button
            type="default"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            aria-label="Menüyü aç"
            className={styles.panelHeaderMenuBtn}
          >
            <span className={styles.panelHeaderMenuLabel}>Menü</span>
          </Button>
          <Button type="text" icon={<HomeOutlined />} onClick={() => navigate('/')} className={styles.panelHeaderHomeBtn}>
            Anasayfa
          </Button>
        </div>
        <div className="panel-header-right">
          <span className={styles.panelHeaderTitle}>Panel</span>
        </div>
      </header>

      <div className={styles.profileWrapper}>
        <Layout className={styles.profileLayout}>
          <Sider
            width={260}
            className={`${styles.sidebar} panel-sider-dark`}
            breakpoint="lg"
            collapsedWidth={0}
            trigger={null}
            collapsed={false}
            style={{ display: 'block' }}
          >
            {renderSidebar()}
          </Sider>
          <Layout className={styles.contentLayout}>
            <Content className={styles.contentArea}>
              {selectedMenu === 'profile' && (
                <SubscriptionProtectedRoute allowUnsubscribed={true}>
                  <ProfileTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'subscription' && (
                <SubscriptionProtectedRoute allowUnsubscribed={true}>
                  <SubscriptionTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'settings' && (
                <SubscriptionProtectedRoute allowUnsubscribed={true}>
                  <SettingsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'education' && (
                <SubscriptionProtectedRoute>
                  <EducationTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'projects' && (
                <SubscriptionProtectedRoute>
                  <ProjectTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'skills' && (
                <SubscriptionProtectedRoute>
                  <SkillTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'work' && (
                <SubscriptionProtectedRoute>
                  <WorkExperienceTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'certificates' && (
                <SubscriptionProtectedRoute>
                  <CertificateTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'job-applications' && (
                <SubscriptionProtectedRoute>
                  <JobApplicationsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'interviews' && (
                <SubscriptionProtectedRoute>
                  <InterviewsTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'referanslar' && (
                <SubscriptionProtectedRoute>
                  <ReferanslarTab />
                </SubscriptionProtectedRoute>
              )}
              {selectedMenu === 'appointments' && (
                <SubscriptionProtectedRoute>
                  <PanelTabCard
                    title="Randevularım"
                    description="Bire-bir, bootcamp ve referans talepleriniz."
                  >
                    <Tabs
                      defaultActiveKey="one-on-one"
                      type="card"
                      size="large"
                      style={{ marginTop: '16px' }}
                      tabBarStyle={{ marginBottom: '24px', borderBottom: '1px solid #e5e7eb' }}
                    >
                      <TabPane tab="Bire-Bir Taleplerim" key="one-on-one">
                        <OneOnOneRequestsTab />
                      </TabPane>
                      <TabPane tab="Bootcamp Taleplerim" key="bootcamp">
                        <BootcampRequestsTab />
                      </TabPane>
                      <TabPane tab="Referans Talepleri" key="reference-requests">
                        <ReferenceRequestsTab />
                      </TabPane>
                      <TabPane tab="Gelen Referans Talepleri" key="incoming-reference-requests">
                        <IncomingReferenceRequestsTab />
                      </TabPane>
                      <TabPane tab="Mentör Değerlendirme" key="mentor-rating">
                        <MentorRatingTab />
                      </TabPane>
                      <TabPane tab="Bana Yazılan Referanslar" key="received-references">
                        <ReceivedReferencesTab />
                      </TabPane>
                    </Tabs>
                  </PanelTabCard>
                </SubscriptionProtectedRoute>
              )}
            </Content>
          </Layout>
        </Layout>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        title="Menü"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={260}
        styles={{ header: { borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#f8fafc', background: '#1e293b' }, body: { padding: 0, background: '#1e293b' } }}
        maskClosable
        closable
        className={styles.mobileDrawer}
      >
        <div style={{ padding: '24px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {renderSidebar()}
        </div>
      </Drawer>
    </Layout>
  );
};

export default MenteePanel;
